import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getIdToken } from '@pooflabs/web';
import { getManyTenants, getTenants } from '@/lib/collections/tenants';
import { getManySessions, getSessions } from '@/lib/collections/sessions';
import { getManyRevenue, getRevenue } from '@/lib/collections/revenue';
import { getManyWebhookDeliveries, getWebhookDeliveries } from '@/lib/collections/webhookDeliveries';
import { DEMO_TENANT_APPID } from '@/lib/constants';
import { api } from '@/lib/api-client';
import { getPoofAPIUrl } from '@/lib/config';
import { MetricCard } from './MetricCard';
import { SessionsTable } from './SessionsTable';
import { RevenueList } from './RevenueList';
import { DeliveriesList } from './DeliveriesList';
import { CreateSessionModal } from './CreateSessionModal';
import { CopyableMono } from './CopyableMono';
import { RegisterPoofyAppDialog, type OneTimeIntegrationResult } from './RegisterPoofyAppDialog';
import { PromoCodesPanel } from './PromoCodesPanel';
import { WalletButton } from './WalletButton';
import { clearPoofSession } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Search, Bell, Plus, KeyRound, ClipboardCopy, MoreHorizontal, CreditCard, Ticket, LogOut } from 'lucide-react';
import { PoofyPayMark } from './PoofyPayMark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const DEMO_OWNER = 'HKbZbRR7jWWR5VRN8KFjvTCHEzJQgameYxKQxh2gPoof';
const OPERATOR_TEST_TENANT_APPID = 'poofy-pay-operator-test';
const OPERATOR_TEST_OWNER = '82SwasnANxmSRnm1DEyUsYGYQUPU21x2yF4M7DpKSyX';
const DEMO_SESSION_IDS = ['sess_demo_crypto', 'sess_demo_whop', 'sess_demo_expired', 'sess_demo_settled'];
const DEMO_REVENUE_ID = 'rev_demo_settled';
const DEMO_DELIVERY_ID = 'wh_demo_pending';

type NavItem = 'Payments' | 'Promo Codes' | 'Payouts' | 'Customers' | 'Balances' | 'Settings';
const NAV_ITEMS: NavItem[] = ['Payments', 'Promo Codes', 'Payouts', 'Customers', 'Balances', 'Settings'];
const ENABLED_NAV = new Set<NavItem>(['Payments', 'Promo Codes']);

const CHECKOUT_PATH_RE = /^\/pay\/[^/\s?]+(\/(success|expired))?(\?\S*)?$/;

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, login, logout, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState<any[]>([]);
  const [activeTenant, setActiveTenant] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [revenue, setRevenue] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [apiKey, setApiKey] = useState<string>('');
  const [activeNav, setActiveNav] = useState<NavItem>('Payments');
  const [createOpen, setCreateOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [issuingKey, setIssuingKey] = useState(false);
  const [oneTimeIntegration, setOneTimeIntegration] = useState<OneTimeIntegrationResult | null>(null);
  const [search, setSearch] = useState('');
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('poofy-pay:apikey:')) localStorage.removeItem(key);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    if (!user?.address) {
      setTenants([]);
      setActiveTenant(null);
      return;
    }
    const addr = user.address;
    const all = await getManyTenants();
      let owned = all.filter((t: any) => t.owner === addr);
      // Fallback: if the collection list returned empty, try direct single-doc reads
      if (owned.length === 0) {
        const fallbacks: any[] = [];
        // Include operator tenant only when the connected wallet owns it
        if (addr === OPERATOR_TEST_OWNER) {
          const operatorTenant = await getTenants(OPERATOR_TEST_TENANT_APPID);
          if (operatorTenant && operatorTenant.owner === addr) {
            fallbacks.push(operatorTenant);
          }
        }
        // Include demo tenant only for the designated demo owner
        if (addr === DEMO_OWNER) {
          const demoTenant = await getTenants(DEMO_TENANT_APPID);
          if (demoTenant) {
            fallbacks.push(demoTenant);
          }
        }
        owned = fallbacks;
      }
      setTenants(owned);
      if (owned.length > 0) {
        setActiveTenant(owned[0]);
        setApiKey('');
      }
  }, [user?.address]);

  // Load tenants when user connects
  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  // Load data for active tenant
  useEffect(() => {
    if (!activeTenant?.appid) return;
    const appid = activeTenant.appid;
    setDataLoading(true);

    const isDemo = appid === DEMO_TENANT_APPID;
    const mockAuth = isDemo && new URLSearchParams(window.location.search).get('mockAuth') === 'true';

    Promise.all([
      getManySessions(),
      getManyRevenue(),
      getManyWebhookDeliveries(),
    ]).then(async ([allSessions, allRevenue, allDeliveries]) => {
      let filteredSessions = allSessions.filter((s: any) => s.appid === appid);
      let filteredRevenue = allRevenue.filter((r: any) => r.appid === appid);
      let filteredDeliveries = allDeliveries.filter((d: any) => d.appid === appid);

      // Demo tenant fallback: per-id reads when collection list is empty
      if (isDemo && filteredSessions.length === 0) {
        const perIdResults = await Promise.all(DEMO_SESSION_IDS.map((id) => getSessions(id)));
        const directSessions = perIdResults.filter((doc): doc is NonNullable<typeof doc> => doc !== null);

        if (directSessions.length > 0) {
          filteredSessions = directSessions;
        } else if (mockAuth) {
          // Last-resort: public API fallback when mockAuth=true
          const apiResults = await Promise.all(
            DEMO_SESSION_IDS.map(async (id) => {
              try {
                const data: any = await api.get(`/api/v1/sessions/${id}/public`);
                return {
                  id,
                  appid,
                  tenantUserId: data.tenantUserId ?? 'demo',
                  amountUsdCents: data.amountUsdCents ?? 0,
                  accepts: data.accepts ?? 'crypto,whop',
                  chosenRail: data.chosenRail,
                  kind: data.kind,
                  metadata: data.metadata,
                  pdaAddress: (data as any).pdaAddress ?? null,
                  solanaPayReference: data.solanaPayReference,
                  whopCheckoutUrl: data.whopCheckoutUrl,
                  whopOrderId: data.whopOrderId,
                  recipientWallet: data.recipientWallet ?? '',
                  productWebhook: data.productWebhook,
                  successRedirectUrl: data.successRedirectUrl,
                  status: data.status ?? 'pending',
                  txSignature: data.txSignature,
                  sweepSignature: data.sweepSignature,
                  nativeAmount: data.nativeAmount,
                  createdAt: data.createdAt ?? 1745884800,
                  paidAt: data.paidAt,
                  settledAt: data.settledAt,
                  expiresAt: data.expiresAt ?? 1745884800 + 1800,
                  tarobase_created_at: data.createdAt ?? 1745884800,
                } as any;
              } catch {
                return null;
              }
            })
          );
          filteredSessions = apiResults.filter((doc): doc is NonNullable<typeof doc> => doc !== null);
        }
      }

      if (isDemo && filteredRevenue.length === 0) {
        const doc = await getRevenue(DEMO_REVENUE_ID);
        if (doc !== null) filteredRevenue = [doc];
      }

      if (isDemo && filteredDeliveries.length === 0) {
        const doc = await getWebhookDeliveries(DEMO_DELIVERY_ID);
        if (doc !== null) filteredDeliveries = [doc];
      }

      setSessions(filteredSessions);
      setRevenue(filteredRevenue);
      setDeliveries(filteredDeliveries);
    }).finally(() => setDataLoading(false));
  }, [activeTenant?.appid]);

  const handleIssueKey = async () => {
    if (!activeTenant || !user) return;
    setIssuingKey(true);
    try {
      const token = await getIdToken();
      const url = getPoofAPIUrl('/api/v1/tenants/me/rotate-credentials');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Wallet-Address': user.address,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appid: activeTenant.appid,
          webhookUrl: activeTenant.productWebhook,
          backendUrl: activeTenant.backendUrl,
          paymentModel: activeTenant.paymentModel,
        }),
      });
      let json: any = null;
      try { json = await res.json(); } catch { /* not JSON */ }
      if (!res.ok || !json?.success) {
        toast.error(json?.error?.message || `Request failed (${res.status})`);
        return;
      }
      const key = json.data.apiKey ?? json.data.integration?.POOFY_PAY_API_KEY ?? '';
      setApiKey(key);
      setOneTimeIntegration(json.data);
      toast.success('Credentials rotated — copy the integration block now');
    } catch {
      toast.error('Network error');
    } finally {
      setIssuingKey(false);
    }
  };

  const handleIntegrationResult = (result: OneTimeIntegrationResult) => {
    const key = result.apiKey ?? result.integration?.POOFY_PAY_API_KEY ?? '';
    setApiKey(key);
    setOneTimeIntegration(result);
    if (result.tenant) {
      const tenant = result.tenant;
      setTenants((current) => {
        const rest = current.filter((item) => item.appid !== tenant.appid);
        return [tenant, ...rest];
      });
      setActiveTenant(tenant);
    } else {
      void loadTenants();
    }
  };

  const dismissIntegrationBlock = () => {
    setOneTimeIntegration(null);
    setApiKey('');
  };

  const copyIntegrationBlock = async () => {
    if (!oneTimeIntegration?.integrationBlock) return;
    try {
      await navigator.clipboard.writeText(oneTimeIntegration.integrationBlock);
      toast.success('Integration block copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleLogout = async () => {
    try {
      await clearPoofSession(logout);
    } catch {
      toast.error('Could not log out');
    }
  };

  const today = Math.floor(Date.now() / 1000) - 86400;
  const settledToday = revenue.filter((r: any) => r.recordedAt > today);
  const settledTodayUsd = (settledToday.reduce((s: number, r: any) => s + r.amountUsdCents, 0) / 100).toFixed(2);
  const thisWeek = Math.floor(Date.now() / 1000) - 7 * 86400;
  const weekRevenue = revenue.filter((r: any) => r.recordedAt > thisWeek);
  const weekRevenueUsd = (weekRevenue.reduce((s: number, r: any) => s + r.amountUsdCents, 0) / 100).toFixed(2);
  const activeSessions = sessions.filter((s: any) => ['pending', 'rail_chosen', 'payment_detected'].includes(s.status));
  const pendingDeliveries = deliveries.filter((d: any) => d.status === 'pending' || d.status === 'failed');

  const filteredSessions = search
    ? sessions.filter((s: any) =>
        s.id.includes(search) || s.tenantUserId.includes(search) || s.status.includes(search)
      )
    : sessions;

  // Not connected
  if (authLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center' style={{ background: 'var(--pp-bg)' }}>
        <span style={{ color: 'var(--pp-text-subtle)' }}>Loading…</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className='min-h-screen flex items-center justify-center' style={{ background: 'var(--pp-bg)' }}>
        <div
          className='rounded-xl border p-10 flex flex-col items-center gap-5 max-w-sm w-full text-center'
          style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
        >
          <PoofyPayMark size='md' />
          <p className='text-sm' style={{ color: 'var(--pp-text-muted)' }}>
            Connect your wallet to access the merchant dashboard
          </p>
          <Button
            data-testid='dashboard-connect'
            onClick={login}
            className='w-full font-semibold'
            style={{ background: 'var(--pp-action)', color: '#fff' }}
          >
            Connect wallet
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex' style={{ background: 'var(--pp-bg)' }}>
      {/* Sidebar */}
      <aside
        className='hidden md:flex flex-col w-56 border-r flex-shrink-0'
        style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
      >
        {/* Logo */}
        <div className='px-5 py-4 border-b' style={{ borderColor: 'var(--pp-border)' }}>
          <PoofyPayMark size='sm' />
        </div>

        {/* Tenant info */}
        {activeTenant && (
          <div className='px-5 py-3 border-b' style={{ borderColor: 'var(--pp-border)' }}>
            <div className='text-sm font-medium' style={{ color: 'var(--pp-text)' }}>
              {activeTenant.name}
            </div>
            <div className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>Tenant Admin</div>
          </div>
        )}

        {/* Nav */}
        <nav className='flex-1 py-3 px-2 flex flex-col gap-0.5'>
          {NAV_ITEMS.map((item) => {
            const isActive = activeNav === item;
            const isDisabled = !ENABLED_NAV.has(item);
            return (
              <button
                key={item}
                onClick={() => ENABLED_NAV.has(item) && setActiveNav(item)}
                className='relative flex items-center gap-2.5 pl-4 pr-3 py-2 rounded-md text-sm text-left transition-colors w-full'
                style={{
                  background: 'transparent',
                  color: isActive ? 'var(--pp-text)' : 'var(--pp-text-muted)',
                  fontWeight: isActive ? 600 : 400,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {isActive && (
                  <span
                    aria-hidden='true'
                    style={{
                      position: 'absolute',
                      left: 4,
                      top: 6,
                      bottom: 6,
                      width: 2,
                      borderRadius: 2,
                      background: 'var(--pp-action)',
                    }}
                  />
                )}
                {item}
              </button>
            );
          })}
        </nav>

        {/* API key status */}
        {activeTenant && (
          <div className='px-4 py-3 border-t text-xs flex flex-col gap-1' style={{ borderColor: 'var(--pp-border)' }}>
            <div className='flex justify-between'>
              <span style={{ color: 'var(--pp-text-subtle)' }}>API Key</span>
              <span style={{ color: apiKey ? 'var(--pp-settled)' : 'var(--pp-danger)', fontWeight: 500 }}>
                {apiKey ? 'Active' : 'Missing'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span style={{ color: 'var(--pp-text-subtle)' }}>App ID</span>
              <span style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text-muted)', fontSize: '0.68rem' }}>
                {activeTenant.appid.slice(0, 16)}
              </span>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className='flex-1 flex flex-col min-w-0'>
        {/* Topbar */}
        <header
          className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between px-4 md:px-6 py-3 border-b'
          style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
        >
          {/* Mobile-only brand + overflow row (sidebar is hidden on <md) */}
          <div className='flex items-center justify-between md:hidden'>
            <PoofyPayMark size='sm' />
            <div className='flex items-center gap-1.5'>
              <button
                style={{ color: 'var(--pp-text-muted)' }}
                aria-label='Notifications'
              >
                <Bell size={17} strokeWidth={1.8} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className='h-8 w-8 rounded-md border flex items-center justify-center'
                    style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)' }}
                    aria-label='Open actions'
                  >
                    <MoreHorizontal size={17} strokeWidth={1.8} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-56'>
                  <DropdownMenuItem onSelect={() => setActiveNav('Payments')}>
                    <CreditCard size={14} className='mr-2' />
                    Payments
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setActiveNav('Promo Codes')}>
                    <Ticket size={14} className='mr-2' />
                    Promo codes
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRegisterOpen(true)}>
                    <Plus size={14} className='mr-2' />
                    Register Poofy app
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => setCreateOpen(true)}
                    disabled={!apiKey}
                  >
                    <Plus size={14} className='mr-2' />
                    Create session
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid='dashboard-logout-mobile' onSelect={handleLogout}>
                    <LogOut size={14} className='mr-2' />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Search — full-width on mobile, fixed on desktop */}
          <div className='relative flex items-center w-full md:w-auto'>
            <Search size={14} className='absolute left-2.5' style={{ color: 'var(--pp-text-subtle)' }} />
            <Input
              placeholder='Search sessions…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const trimmed = search.trim();
                  if (CHECKOUT_PATH_RE.test(trimmed)) {
                    setSearch('');
                    navigate(trimmed);
                  }
                }
              }}
              className='pl-8 h-8 text-sm w-full md:w-56'
              style={{ borderColor: 'var(--pp-border)', background: 'transparent' }}
            />
          </div>

          {/* Desktop-only action cluster */}
          <div className='hidden md:flex items-center gap-3'>
            <button style={{ color: 'var(--pp-text-muted)' }} aria-label='Notifications'>
              <Bell size={17} strokeWidth={1.8} />
            </button>
            {activeTenant && (
              <div
                className='text-xs px-2.5 py-1 rounded border'
                style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)', fontFamily: 'var(--pp-mono)' }}
              >
                {activeTenant.name}
              </div>
            )}
            <Button
              onClick={() => setRegisterOpen(true)}
              variant='outline'
              className='h-8 text-xs font-semibold gap-1.5'
            >
              <Plus size={14} />
              Register Poofy app
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!apiKey}
              className='h-8 text-xs font-semibold gap-1.5'
              style={{ background: 'var(--pp-action)', color: '#fff' }}
            >
              <Plus size={14} />
              Create session
            </Button>
            <WalletButton />
          </div>
        </header>

        {/* Content */}
        <main className='flex-1 p-5 md:p-6 overflow-auto'>
          {!activeTenant ? (
            <div className='flex flex-col items-center gap-4 py-16 text-center'>
              <p className='text-sm' style={{ color: 'var(--pp-text-muted)' }}>
                Wallet detected ({user.address.slice(0, 4)}…{user.address.slice(-4)}) — no tenant is assigned to this wallet.
              </p>
              <div className='flex items-center gap-3'>
                <Button
                  onClick={() => setRegisterOpen(true)}
                  className='gap-2 font-semibold'
                  style={{ background: 'var(--pp-action)', color: '#fff' }}
                >
                  <Plus size={14} />
                  Register Poofy app
                </Button>
                <Button
                  data-testid='dashboard-logout'
                  onClick={handleLogout}
                  variant='outline'
                  className='gap-2 font-semibold'
                >
                  <LogOut size={14} />
                  Log out
                </Button>
              </div>
            </div>
          ) : (
            <div className='flex flex-col gap-6'>

              {/* Credential rotation banner */}
              {!oneTimeIntegration && (
                <div
                  className='rounded-lg border p-4 flex items-center justify-between gap-3'
                  style={{ background: 'var(--pp-soft-blue)', borderColor: '#BFD5FF' }}
                >
                  <div>
                    <div className='text-sm font-medium' style={{ color: 'var(--pp-text)' }}>
                      Credentials are one-time reveal only
                    </div>
                    <div className='text-xs' style={{ color: 'var(--pp-text-muted)' }}>
                      Rotate credentials to copy a fresh API key and webhook secret integration block.
                    </div>
                  </div>
                  <Button
                    onClick={handleIssueKey}
                    disabled={issuingKey}
                    className='gap-2 text-xs flex-shrink-0'
                    style={{ background: 'var(--pp-action)', color: '#fff' }}
                  >
                    <KeyRound size={13} />
                    {issuingKey ? 'Rotating…' : 'Rotate credentials'}
                  </Button>
                </div>
              )}

              {/* One-time integration block display */}
              {oneTimeIntegration && (
                <div
                  className='rounded-lg border p-4 flex flex-col gap-3'
                  style={{ background: '#F0FDF4', borderColor: '#86EFAC' }}
                >
                  <div className='flex flex-wrap items-center justify-between gap-3'>
                    <div>
                      <div className='text-sm font-medium' style={{ color: 'var(--pp-settled)' }}>
                        Integration block ready — copy now
                      </div>
                      <div className='text-xs' style={{ color: 'var(--pp-text-muted)' }}>
                        Dismissing this block clears the raw API key and webhook secret from the dashboard.
                      </div>
                    </div>
                    <Button type='button' variant='outline' className='h-8 gap-2 text-xs' onClick={copyIntegrationBlock}>
                      <ClipboardCopy size={13} />
                      Copy block
                    </Button>
                  </div>
                  <Textarea
                    readOnly
                    value={oneTimeIntegration.integrationBlock}
                    className='min-h-32 bg-white font-mono text-xs'
                  />
                  <button
                    className='text-xs self-end'
                    style={{ color: 'var(--pp-text-muted)' }}
                    onClick={dismissIntegrationBlock}
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {activeNav === 'Promo Codes' ? (
                <PromoCodesPanel
                  appid={activeTenant.appid}
                  tenantName={activeTenant.name}
                  walletAddress={user.address}
                />
              ) : (
              <>
              {/* Metrics */}
              <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
                <MetricCard label='Settled today'     value={`$${settledTodayUsd}`} accent='var(--pp-settled)' />
                <MetricCard label='Active sessions'   value={String(activeSessions.length)} />
                <MetricCard label='Revenue this week' value={`$${weekRevenueUsd}`} accent='var(--pp-settled)' />
                <MetricCard
                  label='Webhook retries'
                  value={String(pendingDeliveries.length)}
                  accent={pendingDeliveries.length > 0 ? 'var(--pp-warning)' : undefined}
                />
              </div>

              {/* Main panels */}
              <div className='flex flex-col lg:flex-row gap-5'>

                {/* Sessions table */}
                <div className='flex-1 flex flex-col gap-3 min-w-0'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-semibold' style={{ color: 'var(--pp-text)' }}>
                      Recent Sessions
                    </span>
                    <span className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
                      {filteredSessions.length} total
                    </span>
                  </div>
                  <SessionsTable sessions={filteredSessions.slice(0, 15)} />
                </div>

                {/* Right panels */}
                <div className='flex flex-col gap-5 w-full lg:w-72 flex-shrink-0'>
                  {/* Revenue */}
                  <div
                    className='rounded-lg border p-4 flex flex-col gap-3'
                    style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
                  >
                    <span className='text-sm font-semibold' style={{ color: 'var(--pp-text)' }}>
                      Revenue
                    </span>
                    <RevenueList rows={revenue} />
                  </div>

                  {/* Webhook deliveries */}
                  <div
                    className='rounded-lg border p-4 flex flex-col gap-3'
                    style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
                  >
                    <span className='text-sm font-semibold' style={{ color: 'var(--pp-text)' }}>
                      Webhook Deliveries
                    </span>
                    <DeliveriesList deliveries={deliveries} apiKey={apiKey} />
                  </div>

                  {/* Tenant info strip */}
                  {activeTenant && (
                    <div
                      className='rounded-lg border p-4 text-xs flex flex-col gap-2'
                      style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
                    >
                      <div className='flex justify-between'>
                        <span style={{ color: 'var(--pp-text-subtle)' }}>App ID</span>
                        <span style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text-muted)' }}>
                          {activeTenant.appid}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span style={{ color: 'var(--pp-text-subtle)' }}>Recipient</span>
                        <CopyableMono value={activeTenant.defaultRecipientWallet ?? ''} truncate />
                      </div>
                      <div className='flex justify-between'>
                        <span style={{ color: 'var(--pp-text-subtle)' }}>Rails</span>
                        <span style={{ color: 'var(--pp-text-muted)' }}>
                          {activeTenant.defaultAcceptedRails}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span style={{ color: 'var(--pp-text-subtle)' }}>Webhook</span>
                        <span style={{ color: activeTenant.productWebhook ? 'var(--pp-settled)' : 'var(--pp-danger)' }}>
                          {activeTenant.productWebhook ? 'Endpoint set' : 'Missing'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Create session modal */}
      {activeTenant && (
        <CreateSessionModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          apiKey={apiKey}
          appid={activeTenant.appid}
        />
      )}
      <RegisterPoofyAppDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        onRegistered={handleIntegrationResult}
      />
    </div>
  );
};

export default DashboardPage;
